import { useEffect, useState } from 'react'
import { api, pctValue, type VendorRiskTypeRow, type VendorRiskTypes as RiskTypeView } from '../lib/api'
import { BandWithAttestation } from './BandChip'
import { CoverageCell, DataTable, ErrorBox, Loading, type Column } from './DataTable'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// Each gap names the one thing that closes it. The API says which gap a type
// is in; the words are here because they are the console's, not the engine's,
// and because a reader who sees "not scored" without "score it" has been told
// a fact and denied the next step.
const GAP: Record<string, { band: string; next: string }> = {
  NO_SECTION: {
    band: 'Not covered',
    next: 'Add a section of this type in the studio and publish the version',
  },
  NO_ASSESSMENT: {
    band: 'No assessment',
    next: 'Issue an assessment that covers it, through a campaign or a trigger',
  },
  NOT_SCORED: {
    band: 'Not scored',
    next: 'Score the assessment, enough answers are in to try',
  },
  BELOW_FLOOR: {
    band: 'Below the floor',
    next: 'Gather evidence, too few answers came back to claim a band',
  },
}

function gapWords(gap: string | null): { band: string; next: string } | null {
  if (!gap) return null
  return (
    GAP[gap] ?? {
      band: gap.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
      next: 'The (i) beside the count says what this gap means',
    }
  )
}

// Sorting by band puts the types that need a decision at one end, in the
// order the rollup uses: an unknown band is not a good one.
const BAND_RANK: Record<string, number> = {
  CRITICAL: 5,
  INSUFFICIENT_EVIDENCE: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
}

export function VendorRiskTypes({ vendorId }: { vendorId: string }) {
  const [view, setView] = useState<RiskTypeView | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    setView(null)
    setError(null)
    api
      .vendorRiskTypes(vendorId)
      .then((v) => {
        if (live) setView(v)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  if (error != null) return <ErrorBox error={error} what="The risk type coverage" />
  if (!view) return <Loading what="the risk type coverage" />

  const columns: Column<VendorRiskTypeRow>[] = [
    {
      key: 'type',
      header: 'Risk type',
      sortValue: (r) => r.name,
      render: (r) => (
        <>
          <span className="cell-primary">{r.name}</span>
          <span className="cell-sub mono">{r.riskType}</span>
        </>
      ),
    },
    {
      key: 'coveredBy',
      header: 'Covered by',
      sortValue: (r) => r.coveredBy?.questionnaireCode ?? null,
      render: (r) =>
        r.coveredBy ? (
          <>
            <span className="mono">{r.coveredBy.sectionKeys.join(', ') || 'section keys not named'}</span>
            <span className="cell-sub">
              {r.coveredBy.questionnaireCode}, {r.coveredBy.questionCount} question
              {r.coveredBy.questionCount === 1 ? '' : 's'}
            </span>
          </>
        ) : (
          <span className="muted">no section carries this type</span>
        ),
    },
    {
      key: 'band',
      header: 'Band',
      sortValue: (r) => (r.score ? (BAND_RANK[r.score.finalBand] ?? 0) : null),
      render: (r) =>
        r.score ? (
          <BandWithAttestation band={r.score.finalBand} attestationPct={r.score.attestationPct} />
        ) : (
          <Status tone="neutral">{gapWords(r.gap)?.band ?? 'Not scored'}</Status>
        ),
    },
    {
      key: 'coverage',
      header: 'Coverage',
      numeric: true,
      sortValue: (r) => (r.score ? pctValue(r.score.coveragePct) : null),
      render: (r) => <CoverageCell value={r.score ? pctValue(r.score.coveragePct) : null} />,
    },
    {
      key: 'next',
      header: 'What to do',
      render: (r) => {
        const gap = gapWords(r.gap)
        if (gap) return gap.next
        if (r.score) return <span className="muted">Nothing, keep the cadence</span>
        return <span className="muted">Nothing the API can name</span>
      },
    },
  ]

  return (
    <section className="panel panel-flush" aria-labelledby="risk-heading">
      <div className="panel-head">
        <h2 id="risk-heading" style={{ margin: 0 }}>
          Risk types
        </h2>
        {/* The note is the API's own sentence about how these readings were
            derived, behind the (i) beside the count it explains. */}
        <p className="summary-line sw-row" style={{ margin: 0 }} role="status">
          <span>
            <strong>{view.covered}</strong> of <strong>{view.total}</strong> risk types covered
          </span>
          <ReasonTip
            label="How the vendor band is read"
            text={
              view.note ||
              'The vendor band is the worst of the assessment band and every type band. A type with no band does not move it.'
            }
          />
        </p>
      </div>

      <DataTable
        caption={'Risk type coverage for ' + (view.vendor.name || view.vendor.vendorCode || 'this vendor') + ', in catalogue order.'}
        columns={columns}
        rows={view.riskTypes}
        rowKey={(r) => r.riskType}
        empty={{
          title: 'No risk type has a reading for this vendor',
          detail: (
            <>
              Give a questionnaire section a risk type and publish the version{' '}
              <Toggletip label="When a type gets a band">
                A type gets a band once a questionnaire section carries it and an assessment on that questionnaire is
                scored.
              </Toggletip>
            </>
          ),
        }}
      />
    </section>
  )
}
